import type { TFunction } from 'i18next'
import { z } from 'zod'

/* eslint-disable @typescript-eslint/naming-convention */
export const getReviewEntryLineItemFormSchema = (t: TFunction) =>
  z.object({
    // string fields fed by `getDefaultReviewEntryLineItem` receive `undefined` on newly-added
    // lines, so surface the same i18n copy for the "required" / "invalid type" cases that
    // `.min(1, ...)` handles for the empty-string case
    accountId: z
      .string({
        invalid_type_error: t('review_entries.detail.error.account_required'),
        required_error: t('review_entries.detail.error.account_required'),
      })
      .min(1, t('review_entries.detail.error.account_required')),
    accountName: z.string().optional(),
    accountNumber: z
      .string({
        invalid_type_error: t('review_entries.detail.error.account_required'),
        required_error: t('review_entries.detail.error.account_required'),
      })
      .min(1, t('review_entries.detail.error.account_required')),
    amountExclVat: z.number({
      invalid_type_error: t('review_entries.detail.error.amount_excl_vat_required'),
      required_error: t('review_entries.detail.error.amount_excl_vat_required'),
    }),
    amountInclVat: z.number({
      invalid_type_error: t('review_entries.detail.error.amount_incl_vat_required'),
      required_error: t('review_entries.detail.error.amount_incl_vat_required'),
    }),
    description: z.string().optional(),
    side: z.enum(['credit', 'debit']).optional(),
    taxRate: z.number({
      invalid_type_error: t('review_entries.detail.error.tax_rate_required'),
      required_error: t('review_entries.detail.error.tax_rate_required'),
    }),
    vatAmount: z.number().optional(),
    vatAccountNumber: z.string().nullable().optional(),
    vatRateId: z
      .string({
        invalid_type_error: t('review_entries.detail.error.vat_rate_required'),
        required_error: t('review_entries.detail.error.vat_rate_required'),
      })
      .min(1, t('review_entries.detail.error.vat_rate_required')),
  })

export const getReviewEntryDataFormSchema = (t: TFunction) =>
  z.object({
    currencyId: z.string().min(1, t('review_entries.detail.error.currency_required')),
    dueDate: z.date().nullable().optional(),
    entryLines: z.array(getReviewEntryLineItemFormSchema(t)).optional(),
    invoiceNumber: z.string().min(1, t('review_entries.detail.error.invoice_number_required')),
    receiptDate: z.date({
      invalid_type_error: t('review_entries.detail.error.receipt_date_required'),
      required_error: t('review_entries.detail.error.receipt_date_required'),
    }),
    // the field holds the whole contact (like the bill form `vendor` field) so `ContactSelect`
    // can display an AI-extracted supplier name before the contact exists; a persisted `id` is
    // what makes the entry valid — an unmatched `{ name }` still fails with supplier_required.
    // `null` (not `undefined`) is the cleared state: react-hook-form falls back to the field's
    // defaultValue when a controlled value is set to `undefined`, which would resurrect the
    // prefilled supplier after the user clears the select.
    supplier: z
      .object({ id: z.string().optional(), name: z.string().optional() })
      .nullish()
      .refine((supplier) => !!supplier?.id, t('review_entries.detail.error.supplier_required')),
  })
/* eslint-enable @typescript-eslint/naming-convention */

export type ReviewEntryDataFormSchema = ReturnType<typeof getReviewEntryDataFormSchema>
export type ReviewEntryLineItemFormValues = z.infer<ReturnType<typeof getReviewEntryLineItemFormSchema>>
export type ReviewEntryDataFormValues = z.infer<ReturnType<typeof getReviewEntryDataFormSchema>>
